/* eslint-disable no-loop-func */
// Custom services
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { ApolloLink, Observable } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import { onError } from 'apollo-link-error';
import { createUploadLink } from 'apollo-upload-client';

import authService from '../Auth';

let isRefreshing = false;
let pendingRequests = [];

const logout = () => {
  authService.logout({
    redirectMessage: 'Merci de vous reconnecter.',
  });

  window.location.reload();
};

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = authService.getToken();

  if (token === null) {
    // handle redirect to login
    authService.logout({ redirectMessage: 'Merci de vous connecter pour accéder au service.' });
  }

  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

function getClientOptions(withCors = true) {
  const options = {
    link: ApolloLink.from([
      // eslint-disable-next-line consistent-return
      onError(({ graphQLErrors, networkError, operation, forward }) => {
        if (graphQLErrors) {
          for (let error of graphQLErrors) {
            const { message, locations, path, extensions } = error;
            console.log(`[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`);

            switch (extensions.code) {
              case 'UNAUTHENTICATED':
                if (!isRefreshing) {
                  isRefreshing = true;
                  if (authService.canRefreshToken()) {
                    return new Observable(observer => {
                      authService
                        .tokenRefresh()
                        .then(accessToken => {
                          operation.setContext(({ headers = {} }) => ({
                            headers: {
                              // Re-add old headers
                              ...headers,
                              // Switch out old access token for new one
                              authorization: `Bearer ${accessToken}` || null,
                            },
                          }));

                          return authService.handleAuthentication({ accessToken, logoutIfNoUserFound: false });
                        })
                        .then(() => {
                          const subscriber = {
                            next: observer.next.bind(observer),
                            error: observer.error.bind(observer),
                            complete: observer.complete.bind(observer),
                          };

                          // Retry last failed request
                          forward(operation).subscribe(subscriber);
                        })
                        .catch(error => {
                          // No refresh or client token available, we force user to login
                          observer.error(error);

                          logout();
                        })
                        .finally(() => {
                          isRefreshing = false;

                          pendingRequests.forEach(resolve => resolve());
                          pendingRequests = [];
                        });
                    });
                  } else {
                    logout();

                    return false;
                  }
                } else {
                  return new Observable(observer => {
                    pendingRequests.push(() => {
                      const subscriber = {
                        next: observer.next.bind(observer),
                        error: observer.error.bind(observer),
                        complete: observer.complete.bind(observer),
                      };

                      // Retry last failed request
                      forward(operation).subscribe(subscriber);
                    });
                  });
                }
              default:
                break;
            }
          }
        }
        if (networkError) console.log(`[Network error]: ${networkError}`);
      }),
      authLink,
      createUploadLink({ uri: process.env.REACT_APP_GRAPHQL_ENDPOINT }),
    ]),
    cache: new InMemoryCache({
      typePolicies: {
        HonorairesMensuel: {
          keyFields: ['id', 'honoraires'],
        },
        TransactionCoach: {
          keyFields: ['id', 'dateEffectiveSignature', 'filiere'],
        },
      },
    }),
  };

  if (!withCors) {
    options.fetchOptions = {
      mode: 'no-cors',
    };
  }

  return options;
}

const client = new ApolloClient(getClientOptions(false));

export default client;
